import type { ReactNode } from 'react';

/**
 * Broker-curated comparable sales (v2 design §4). Renders ONLY what deal.json carries -
 * the compliance boundary is upstream (curation at authoring, tier filter at the API), so
 * this component is a dumb table: no lookups, no derivation, notes verbatim.
 */

export interface CompItem {
  id: string;
  address: string;
  url?: string;
  area?: string;
  detail?: string;
  lot?: string;
  soldDate?: string;
  onMarket?: string;
  acres?: number;
  salePrice?: number;
  askPrice?: number;
  perAcre?: number;
  perSf?: number;
  note?: string;
}

export interface CompGroup {
  key?: string;
  label?: string;
  source?: string;
  items?: CompItem[];
  footnote?: string;
}

export interface CompsBlock {
  /**
   * The section's own name, when the deal's comps are not sales - Mario's portal (305 S 9th Ave,
   * 2026-10-26) carries lease comparables, and "Comparable sales" over a table of signed leases is wrong.
   * Absent or blank keeps the original heading, so every existing portal reads exactly as before.
   */
  heading?: string;
  asOf?: string;
  intro?: string;
  groups?: CompGroup[];
  footnote?: string;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const money = (n?: number) => (typeof n === 'number' && Number.isFinite(n) ? USD.format(n) : null);
const num = (n?: number) =>
  typeof n === 'number' && Number.isFinite(n)
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)
    : null;

/** Canonical column order; a column renders only when some item in the group carries it. */
const COLUMNS: Array<{ key: keyof CompItem; label: string; fmt: (i: CompItem) => ReactNode }> = [
  { key: 'area', label: 'Area', fmt: (i) => i.area },
  { key: 'detail', label: 'Detail', fmt: (i) => i.detail },
  { key: 'lot', label: 'Lot', fmt: (i) => i.lot },
  { key: 'soldDate', label: 'Sold', fmt: (i) => i.soldDate },
  { key: 'onMarket', label: 'On market', fmt: (i) => i.onMarket },
  { key: 'acres', label: 'Acres', fmt: (i) => num(i.acres) },
  { key: 'salePrice', label: 'Sale price', fmt: (i) => money(i.salePrice) },
  { key: 'askPrice', label: 'Ask', fmt: (i) => money(i.askPrice) },
  { key: 'perAcre', label: '$/AC', fmt: (i) => money(i.perAcre) },
  { key: 'perSf', label: '$/SF', fmt: (i) => money(i.perSf) },
  { key: 'note', label: 'Broker note', fmt: (i) => i.note },
];

const NUMERIC = new Set<keyof CompItem>(['acres', 'salePrice', 'askPrice', 'perAcre', 'perSf']);

/**
 * The render-side third layer on comp links. The import gate already rejects a non-https
 * url and the tier filter never invents one, but a link is the only thing on this page that
 * can take a client OFF it - so anything that is not plainly https prints as text instead of
 * becoming an anchor. `javascript:`, `data:` and bare `http:` all fail this and render inert.
 */
const HTTPS_URL = /^https:\/\//;

export default function ComparableSales({
  comps,
  renderRowExtra,
}: {
  comps: CompsBlock | undefined;
  renderRowExtra?: (item: CompItem) => ReactNode;
}) {
  const groups = (comps?.groups ?? []).filter((g) => (g.items ?? []).length > 0);
  if (!groups.length) return null;

  return (
    // The anchor id lives here rather than on a wrapper in the page: this component owns
    // whether the section renders at all, so the jump menu's Comps target is minted by the
    // same early return that decides the section exists (v2.1 §1).
    <section className="portal-section" id="section-comps">
      <h2>{comps?.heading?.trim() || 'Comparable sales'}</h2>
      {comps?.intro && <p className="portal-headline">{comps.intro}</p>}
      {comps?.asOf && <p className="portal-note">As of {comps.asOf}</p>}

      {groups.map((g, gi) => {
        const items = g.items ?? [];
        const cols = COLUMNS.filter((c) => items.some((i) => i[c.key] !== undefined && i[c.key] !== null));
        return (
          <div className="portal-comp-group" key={g.key ?? gi}>
            <h3>{g.label}</h3>
            {g.source && <p className="portal-comp-source">Source: {g.source}</p>}
            <div className="portal-table-scroll">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th scope="col">Property</th>
                    {cols.map((c) => (
                      <th scope="col" key={c.key} className={NUMERIC.has(c.key) ? 'portal-num' : undefined}>
                        {c.label}
                      </th>
                    ))}
                    {renderRowExtra && <th scope="col" aria-label="Notes" />}
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id} id={`comp-${i.id}`}>
                      <td>
                        {i.url && HTTPS_URL.test(i.url) ? (
                          <a
                            className="portal-comp-link"
                            href={i.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                          >
                            {i.address} ↗
                          </a>
                        ) : (
                          i.address
                        )}
                      </td>
                      {cols.map((c) => (
                        <td key={c.key} className={NUMERIC.has(c.key) ? 'portal-num' : undefined}>
                          {c.fmt(i) ?? '—'}
                        </td>
                      ))}
                      {renderRowExtra && <td className="portal-note-cell">{renderRowExtra(i)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {g.footnote && <p className="portal-note">{g.footnote}</p>}
          </div>
        );
      })}

      {comps?.footnote && <p className="portal-note">{comps.footnote}</p>}
    </section>
  );
}
